import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Joyride, STATUS } from "react-joyride";
import { useAuth } from "@/lib/auth";

const STORAGE_KEY = "hireloop:onboarding-tour-done";

// Each step targets a real `data-testid` on the dashboard. The tour will
// silently no-op if the target element doesn't exist (e.g. trial-only user
// without jobs yet) — handled via the runtime skip logic below.
const STEPS = [
  {
    target: "body",
    placement: "center",
    title: "Welcome to Hireloop!",
    content:
      "Quick 60-second tour of the 6 things you'll do most. Use the arrow keys or click Next to advance. You can replay this anytime from /learn.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="create-job-btn"]',
    title: "1. Create your first job",
    content:
      "Click here to spin up a new role. Paste the JD and Hireloop auto-tailors interview questions.",
  },
  {
    target: '[data-testid="dashboard-jobs-grid"]',
    title: "2. Your job pipeline",
    content:
      "Every job lives here with a quick glance at applicants, scores, and engagement. Click any job to dive in.",
  },
  {
    target: '[data-testid="dashboard-email-engagement-card"]',
    title: "3. Email engagement at a glance",
    content:
      "See opens · clicks · bounces · time-to-respond across every invite — sort applicants by who clicked the invite first.",
  },
  {
    target: '[data-testid="nav-team"]',
    title: "4. Invite your teammates",
    content:
      "Add seats, share branded templates, and split the load. Settings → Team controls everything.",
  },
  {
    target: '[data-testid="nav-settings"]',
    title: "5. Custom branding & manual",
    content:
      "Replace the Hireloop logo with yours, customize emails, and open the searchable 32-section Employer Manual.",
  },
  {
    target: "body",
    placement: "center",
    title: "You're set",
    content:
      'Click any job tile to start uploading résumés. Stuck? Visit /learn or hit "?" in Settings to reopen the manual search.',
  },
];

export default function OnboardingTour() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);

  useEffect(() => {
    if (!user) return; // only logged-in
    if (location.pathname !== "/dashboard") return;

    // /dashboard?tour=1 force-restarts the tour even after completion
    const force = new URLSearchParams(location.search).get("tour") === "1";

    let alreadyDone = false;
    try {
      alreadyDone = localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      /* private mode, ignore */
    }

    if (force || !alreadyDone) {
      // Defer until target elements paint
      const t = setTimeout(() => setRun(true), 800);
      return () => clearTimeout(t);
    }
  }, [user, location.pathname, location.search]);

  const handleCallback = (data) => {
    const { status, type } = data;
    if (
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED ||
      type === "tour:end"
    ) {
      setRun(false);
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      // Strip ?tour=1 from URL so refresh doesn't re-trigger
      if (new URLSearchParams(location.search).get("tour")) {
        navigate("/dashboard", { replace: true });
      }
    }
  };

  if (!run) return null;

  return (
    <Joyride
      steps={STEPS}
      run={run}
      continuous
      showSkipButton
      showProgress
      disableScrolling={false}
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: "#1d4ed8",
          textColor: "#0f172a",
          backgroundColor: "#ffffff",
          arrowColor: "#ffffff",
          zIndex: 9999,
        },
        tooltipContainer: { textAlign: "left" },
        buttonNext: {
          backgroundColor: "#1d4ed8",
          borderRadius: 2,
          fontWeight: 600,
        },
        buttonBack: { color: "#64748b" },
        buttonSkip: { color: "#64748b" },
      }}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        skip: "Skip tour",
      }}
    />
  );
}
